import React from 'react';
import List from 'housecall-ui/dist/DesignSystem/DataDisplay/List/List';
import ListItem from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItem';
import ListItemIcon from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItemIcon';
import ListItemSecondaryAction from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItemSecondaryAction';
import ListItemText from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItemText';
import Tooltip from 'housecall-ui/dist/DesignSystem/DataDisplay/Tooltips/Tooltip';
import IconButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/IconButton';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import CheckIcon from 'housecall-ui/dist/IconsAndImages/Icons/Check';
import InfoOutlinedIcon from 'housecall-ui/dist/IconsAndImages/Icons/InfoOutlined';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { PayWallDescriptionItem } from 'components/PayWall/Redux/Reducer.type';

const useStyles = makeStyles((theme: HCPTheme) => ({
  root: {},
  listItemRoot: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  listItemTextPrimary: {
    fontSize: theme.typography.body2.fontSize,
  },
  listItemTooltip: {
    fontSize: theme.typography.body2.fontSize,
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
  },
  listItemIconRoot: {
    minWidth: theme.spacing(5),
  },
  emphasis: {
    fontWeight: (theme.typography.fontWeightMedium as number) ?? 'normal',
    marginBottom: theme.spacing(1),
  },
}));

type FeaturesListProps = {
  classes?: {
    root?: string;
    emphasis?: string;
    listItemTextPrimary?: string;
    listItemTooltip?: string;
  };
  features: PayWallDescriptionItem[];
  dense?: boolean;
  emphasizeFirst?: boolean;
  firstAsHeader?: boolean;
};

const FeaturesList = ({
  classes: upstreamClasses,
  features = [],
  dense,
  emphasizeFirst = true,
  firstAsHeader = false,
}: FeaturesListProps): JSX.Element => {
  const classes = useStyles({ classes: upstreamClasses, dense });

  return (
    <List className={classes.root} dense={dense ?? false} data-testid="features-list">
      {features.map(({ title, description }, index) => (
        <ListItem key={title} classes={{ root: classes.listItemRoot }}>
          {(!firstAsHeader || index !== 0) && (
            <ListItemIcon classes={{ root: classes.listItemIconRoot }}>
              <CheckIcon color="primary" fontSize={dense ? 'small' : 'default'} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={title}
            classes={{
              primary: `${emphasizeFirst && index === 0 ? classes.emphasis : ''} ${classes.listItemTextPrimary}`,
            }}
          />
          {description && (
            <ListItemSecondaryAction>
              <Tooltip classes={{ tooltip: classes.listItemTooltip }} title={description}>
                <IconButton size={dense ? 'small' : 'medium'} edge="end" aria-label={description}>
                  <InfoOutlinedIcon fontSize={dense ? 'small' : 'inherit'} />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          )}
        </ListItem>
      ))}
    </List>
  );
};

export default FeaturesList;
